import { ArrowUpward, ArrowDownward } from "@mui/icons-material"
import { Stack, Typography, Tooltip, Skeleton, TableRow, TableCell } from "@mui/material"
import { Link as MuiLink } from "@mui/material"
import { useEffect, useState } from "react"

import { Link } from "react-router-dom"

import { getTokenTransfers } from "@/api/funding-api"
import { AsyncTable } from "@/components/AsyncTable"
import { ErrorBoundary } from "@/components/ErrorBoundary"
import { TokenAmountBlock } from "@/components/TokenAmountBlock"
import { REFRESH_INTERVAL } from "@/settings"
import { AoMessage } from "@/types"
import { TokenTransferMessage } from "@/utils/arweave-utils"
import { formatFullDate, formatRelative } from "@/utils/format"

const pageSize = 25

export function EntityFunding(props: { entityId: string; owner: string }) {
  const { owner, entityId } = props
  const [, setRefresh] = useState(0)

  useEffect(() => {
    const interval = setInterval(() => {
      setRefresh(Math.random())
    }, REFRESH_INTERVAL)

    return () => {
      clearInterval(interval)
    }
  }, [])

  return (
    <>
      <AsyncTable
        size="small"
        sx={{
          "& .MuiTableBody-root": {
            maxHeight: 500, // TODO
            overflowY: "auto",
          },
        }}
        headerCells={[]}
        initialSortDir="desc"
        initialSortField={"ingestedAt" satisfies keyof AoMessage}
        pageSize={pageSize}
        fetchFunction={async (offset, ascending, sortField, lastRecord) => {
          const [count, records] = await getTokenTransfers(
            entityId,
            owner,
            pageSize,
            lastRecord?.cursor,
            ascending,
          )

          // if (count !== undefined && onCountReady) {
          //   onCountReady(count)
          // }

          return records
        }}
        renderRow={(transfer: TokenTransferMessage) => {
          return (
            <TableRow
              key={transfer.id}
              sx={{
                "&:nth-of-type(2) > td": {
                  borderTopRightRadius: 20,
                  borderTopLeftRadius: 20,
                },
                "&:last-of-type > td": {
                  borderBottomRightRadius: 20,
                  borderBottomLeftRadius: 20,
                },
              }}
            >
              <TableCell
                sx={{
                  backgroundColor: (theme) => theme.palette.background.paper,
                  borderBottom: "4px solid",
                  borderColor: (theme) => theme.palette.background.default,
                  paddingY: 2,
                }}
              >
                <ErrorBoundary fallback={<>Unknown transfer</>}>
                  <FundingRow key={transfer.id} item={transfer} entityId={entityId} owner={owner} />
                </ErrorBoundary>
              </TableCell>
            </TableRow>
          )
        }}
      />
    </>
  )
}

function FundingRow({
  item,
  entityId,
  owner,
}: {
  item: TokenTransferMessage
  entityId: string
  owner: string
}) {
  const { id, sender, recipient, amount, tokenId, ingestedAt, tags } = item

  const isDeposit = recipient === entityId
  const isWithdrawal = sender === entityId

  const action = isDeposit ? "Deposited" : isWithdrawal ? "Withdrew" : "Transferred"

  const refundMessage = tags["X-Portfolio-Return-Funds-Reason"]

  return (
    <Stack direction="row" gap={1} alignItems="center">
      <Tooltip title={action}>
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{
            borderRadius: 1,
            height: 28,
            width: 28,
            background: isDeposit
              ? "rgba(var(--mui-palette-success-mainChannel) / 0.1)"
              : "rgba(var(--mui-palette-error-mainChannel) / 0.1)",
            border: "1px solid",
            borderColor: isDeposit
              ? "var(--mui-palette-success-main)"
              : "var(--mui-palette-error-main)",
            "& svg": {
              width: "0.85rem",
              height: "0.85rem",
            },
          }}
        >
          {isDeposit ? (
            <ArrowDownward style={{ color: "var(--mui-palette-success-main)" }} />
          ) : (
            <ArrowUpward style={{ color: "var(--mui-palette-error-main)" }} />
          )}
        </Stack>
      </Tooltip>
      <Stack sx={{ flexShrink: 0 }}>
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography variant="body2">
            {action === "Deposited"
              ? "Owner deposited"
              : refundMessage
                ? "Agent refunded"
                : "Owner withdrew"}
          </Typography>
          {amount === undefined ? (
            <Skeleton width={110} />
          ) : (
            <TokenAmountBlock
              amount={amount}
              tokenId={tokenId}
              hideCopyToClipboard
              needsParsing
              showTicker
              color="accent.main"
            />
          )}
          {refundMessage && (
            <>
              <Typography variant="body2">because of</Typography>
              <i>{refundMessage}</i>
            </>
          )}
        </Stack>
        <Typography variant="caption" color="secondary">
          <Tooltip title={formatFullDate(ingestedAt)}>
            <span>{formatRelative(ingestedAt)}</span>
          </Tooltip>{" "}
          •{" "}
          <MuiLink component={Link} to={`/entity/${owner}`} color="secondary" variant="caption">
            View owner
          </MuiLink>{" "}
          •{" "}
          <MuiLink
            href={`https://ao.link/#/message/${id}`}
            target="_blank"
            rel="noopener noreferrer"
            color="secondary"
            variant="caption"
          >
            View on ao.link
          </MuiLink>
        </Typography>
      </Stack>
    </Stack>
  )
}
